import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import PresetActions from '../../../components/preset/presetActions';
import { PresetCard } from '../../../components/preset/PresetCard';
import PresetControllers from '../../../components/preset/presetControllers';
import PresetParameters from '../../../components/preset/presetParameters';
import { SystemProfileTypeIds } from '../../../components/preset/presetSettingsWizard';
import { SelectType } from '../../../components/settingFields/SettingSelectField';
import { toast } from "react-toastify";
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { DevicePreset as ControllerPresetModel } from '../../../models/DevicePreset';
import { DeleteEntityResult } from '../../../models/services/DeleteEntityResult';
import { TabSummary } from '../../../models/TabSummary';
import { getControllerPresetsId, getDeviceProfileNames, storeControllerPreset } from '../../../services/hardwarePresetService';
import { deletePresetSetting } from '../../../services/presetService';
import { getErrorMessage, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from '../../../store/types';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { PRESET_TYPES } from '../../../constants/preset-constants';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const initialItem : ControllerPresetModel = {
    id: 0,
    name: '',
    type: '',
    devices: 0,
    site: '',
    siteId: 0,
    profileTypeId: '',
    pInheritsFrom: '',
    inheritsFromId: 0,
    description: '',
    totalActions: 0,
    totalParameters: 0,
    totalInputs: 0,
    totalDevices: 0,
}

const allItemsPath = '/access-control/hardware/presets';
const itemPath = '/access-control/hardware/controller-preset/';

const ControllerPreset: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.actions), badge: 0, showInitial: true, isActive: false },
        { label: translate.byKey(TranslationKeys.parameters), badge: 0, showInitial: true, isActive: false },
        { label: translate.byKey(TranslationKeys.controllers), badge: 0, showInitial: true, isActive: false },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true }
    ];
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<ControllerPresetModel>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>([]);
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);

    const onDelete = (id: string, index): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deletePresetSetting(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        setTabs(updateTabBadgeTotal(index, tabs[index].badge - 1, tabs));
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    const onPresetSave = (index) => {
        setTabs(updateTabBadgeTotal(index, tabs[index].badge + 1, tabs));
    }

    const tabContentList: ContentWrapper[] = [
        {
            content: <PresetActions currentItemDetails={currentItem} onDelete={(id) => onDelete(id, 0)} onSave={(createNew) => { if (createNew) onPresetSave(0) }} profileId={SystemProfileTypeIds.Controller}
                itemId={itemId} />,
        },
        {
            content: <PresetParameters onSave={(createNew) => { if (createNew) onPresetSave(1) }} profileId={SystemProfileTypeIds.Controller}
                itemId={itemId} />,
        },
        {
            content: <PresetControllers itemId={itemId} />
        },
        {
            content: <Description title={translate.byKey(TranslationKeys.notes)} />
        }
    ];

    const topic: Topic = {
        accessor: 'nameOriginal',
        title: translate.byKey(TranslationKeys.controllerPreset),
        allItemsPath: allItemsPath,
        itemPath: itemPath
    }

    const reloadPage = async () => {
        try {
            setTabs([]);
            let controllerPreset;

            if (itemId) {
                controllerPreset = await getControllerPresetsId(itemId);

                setSelectedItem({label: controllerPreset.name, value: controllerPreset.id.toString(), isDefault: controllerPreset.isDefault})
                setCurrentItem(controllerPreset);
                // update the tab badge counts
                const updatedTabs =
                    updateTabBadgeTotal(0, controllerPreset?.totalActions,
                        updateTabBadgeTotal(1, controllerPreset?.totalParameters,
                            updateTabBadgeTotal(2, controllerPreset?.totalDevices, initialTabs)));
                setTabs(updatedTabs);
            }
            else {
                setCurrentItem(initialItem)
                setTabs([initialTabs[3]]);
            }

            setBreadcrumb([
                { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.preset) },
                { value: itemPath + `${itemId}`, displayName: `${controllerPreset?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    useEffect(() => {
        setTabs([]);
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const getTabContent = (index?: number) => {
        const selectedTabContent = itemId ? tabContentList[index ?? 0] : tabContentList[3];
        return selectedTabContent.content;
    };

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        newTabs[tabIndex].isActive = true; // Set tab to active so we can show it
        return newTabs;
    }

    const saveControllerPreset = (values) => {
        const newControllerPreset: ControllerPresetModel = {
            ...currentItem,
            name: values.name,
            id: values.id,
            pInheritsFrom: values.pInheritsFrom,
            inheritsFromId: values.inheritsFromId,
            siteId: currentItem?.siteId ? currentItem?.siteId : siteId,
            description: values.description
        }

        const isNew = newControllerPreset.id ? false : true;

        setBusy(true);
        storeControllerPreset(newControllerPreset, isNew)
            .then(async (value: any) => {
                setItemId(value.id)
                history.push(`${itemPath}${value.id}`);
                setCurrentItem(newControllerPreset);
                toast.success(translate.byKey(TranslationKeys.controllerPresetSaved));
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }

    return <div className="flex h-full">
        <TopicContextProvider dataMethod={(listParameters) => getDeviceProfileNames(PRESET_TYPES.CONTROLLER, listParameters)} itemId={itemId} topic={topic} selectedItem={selectedItem} >
            <ItemContentTabTwo>
                <SimpleItemDetails
                    breadcrumb={breadcrumb}
                    busy={busy}
                    getTabContent={getTabContent}
                    itemProfile={currentItem} 
                    onBack={() => history.push(allItemsPath)}
                    saveDetails={saveControllerPreset}
                    tabs={tabs}
                    itemPath={itemPath}
                    initialValues={currentItem}>
                    <PresetCard presetType={SelectType.controllerProfile}></PresetCard>
                </SimpleItemDetails>
            </ItemContentTabTwo>
        </TopicContextProvider>
    </div>
}

export default ControllerPreset;
